/**
 * Derive display state for a third-party delivery from the parts_deliveries
 * state pair (orchestration_status + delivery_status). Shared by the runs list
 * (metric buckets, status chips) and the run detail (stepper, live strip) —
 * and by the mock and real clients alike, so keep it vendor-agnostic.
 *
 * Runs fulfilled by a courier do NOT sync mobility_runs.status in v1; this
 * derivation IS the status for external_courier runs.
 */

import type { TpDeliveryStatus, TpOrchestrationStatus } from './types';

export const TP_STEPPER_LABELS = ['Booked', 'Assigned', 'Picked up', 'In transit', 'Delivered'] as const;

export type TpBucket = 'active' | 'completed' | 'cancelled';

export interface TpDerivedStatus {
  /** Short chip/pill label. */
  label: string;
  /** Which LiveOps metric bucket the run counts under. */
  bucket: TpBucket;
  /** 0-based index into TP_STEPPER_LABELS of the last completed node. */
  stepIndex: number;
  /** Booking failed / delivery canceled — style as error. */
  error?: boolean;
  /** Return leg in progress — show the returning chip. */
  returning?: boolean;
  /** Nothing further will happen (delivered/canceled/returned/FAILED). */
  terminal: boolean;
}

/**
 * Map the unified delivery status onto the mobility_stops status vocabulary
 * (pending | in_transit_to | arrived | completed | failed) so a courier run's
 * single stop mirrors the courier's progress. Returns null when the stop
 * shouldn't change yet (pre-pickup states).
 */
export function stopStatusFor(delivery: TpDeliveryStatus | string | null | undefined): string | null {
  switch (delivery) {
    case 'picked_up':
      return 'in_transit_to';
    case 'arriving_dropoff':
      return 'arrived';
    case 'delivered':
      return 'completed';
    case 'canceled':
    case 'returned':
      return 'failed';
    default:
      return null;
  }
}

export function deriveTpStatus(
  orchestration: TpOrchestrationStatus | string | null | undefined,
  delivery: TpDeliveryStatus | string | null | undefined
): TpDerivedStatus {
  // Booking machine first: until BOOKED the vendor status is meaningless.
  switch (orchestration) {
    case 'NEW':
    case 'PENDING':
    case 'BOOKING':
    case 'RETRY':
      return { label: 'Booking…', bucket: 'active', stepIndex: 0, terminal: false };
    case 'FAILED':
      return { label: 'Booking failed', bucket: 'cancelled', stepIndex: 0, error: true, terminal: true };
    default:
      break; // BOOKED (or unknown) → fall through to the delivery lifecycle
  }

  switch (delivery) {
    case 'driver_assigned':
      return { label: 'Assigned', bucket: 'active', stepIndex: 1, terminal: false };
    case 'arriving_pickup':
      return { label: 'Arriving at pickup', bucket: 'active', stepIndex: 1, terminal: false };
    case 'picked_up':
      return { label: 'Picked up', bucket: 'active', stepIndex: 2, terminal: false };
    case 'arriving_dropoff':
      return { label: 'In transit', bucket: 'active', stepIndex: 3, terminal: false };
    case 'delivered':
      return { label: 'Delivered', bucket: 'completed', stepIndex: 4, terminal: true };
    case 'canceled':
      return { label: 'Cancelled', bucket: 'cancelled', stepIndex: 0, error: true, terminal: true };
    case 'return_in_progress':
      return { label: 'Returning', bucket: 'active', stepIndex: 3, returning: true, terminal: false };
    case 'returned':
      return { label: 'Returned', bucket: 'cancelled', stepIndex: 4, returning: true, terminal: true };
    case 'pending':
    default:
      return { label: 'Booked', bucket: 'active', stepIndex: 0, terminal: false };
  }
}
